import React from "react";

import WordButton from "../WordButton";

import * as styles from "./GameGrid.module.css";

import { useSpring, animated } from "react-spring";
import { motion } from "framer-motion";
import { PuzzleDataContext } from "../../providers/PuzzleDataProvider";
import { GameStatusContext } from "../../providers/GameStatusProvider";

import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Badge } from "../ui/badge";


export function SolvedWordRow({ shouldAnimate = false, ...props }) {
  const DIFFICULTY_COLOR_MAP = {
    1: "rgb(250 224 66)", // yellow
    2: "rgb(160 195 90)", // green
    3: "rgb(176 196 239)", // blue
    4: "rgb(186 129 197)", // purple
  };

  const color = `${DIFFICULTY_COLOR_MAP[props.difficulty]}`;

  const [hasBeenClicked, setHasBeenClicked] = React.useState(false);

  const springProps = useSpring({
    from: {
      opacity: 0,
      transform: "scale(0.6)",
      height: 0,
      overflow: 'hidden',
    },
    to: {
      opacity: 1,
      transform: "scale(1)",
      height: 'auto',
      overflow: 'visible',
    },
    config: {
      tension: 280,
      friction: 22,
    },
    delay: shouldAnimate ? 800 : 0,
  });

  const isImageAvailable = props.imageSrc != null;

  const content = !isImageAvailable ? (
    <div style={{ backgroundColor: color, borderRadius: 8 }}>
      <p className="font-semibold pt-2 pl-4 text-gray-900">{props.category}</p>
      <p className="font-normal pb-2 pl-4 text-gray-800">{props.words.join(", ")}</p>
    </div>
  ) : (
    <Popover>
      <PopoverTrigger asChild>
        <div
          className="cursor-pointer hover:animate-pulse shadow-md"
          style={{ backgroundColor: color, borderRadius: 8 }}
          onClick={() => setHasBeenClicked(true)}
        >
          {!hasBeenClicked && (
            <Badge className="animate-pulse absolute top-0 right-0 mr-2 mt-2">
              View More
            </Badge>
          )}
          <p className="font-semibold pt-2 pl-4 text-gray-900">{props.category}</p>
          <p className="font-normal pb-2 pl-4 text-gray-800">{props.words.join(", ")}</p>
        </div>
      </PopoverTrigger>
      <PopoverContent>
        <div>
          <img src={props.imageSrc} />
        </div>
      </PopoverContent>
    </Popover>
  );

  if (!shouldAnimate) {
    return <div>{content}</div>;
  }

  return (
    <animated.div style={springProps}>
      {content}
    </animated.div>
  );
}

function GameGrid({ flatWordsList, shouldGridShake, setShouldGridShake, animatingWords = [], mergingWords = [] }) {
  const { submittedGuesses, isGameOver, isGameWon, solvedGameData } =
    React.useContext(GameStatusContext);

  const { gameData, categorySize } = React.useContext(PuzzleDataContext);

  const initialSolvedCountRef = React.useRef(solvedGameData.length);

  React.useEffect(() => {
    const shakeEffect = window.setTimeout(() => {
      setShouldGridShake(false);
    }, 2000);

    return () => window.clearTimeout(shakeEffect);
  }, [submittedGuesses]);

  const isGameOverAndLost = isGameOver && !isGameWon;
  const isGameOverAndWon = isGameOver && isGameWon;
  const isGameActive = !isGameOver;
  const isGameActiveWithAnySolvedRows =
    isGameActive && solvedGameData.length > 0;

  return (
    <div className="w-full">
      {(isGameOverAndWon || isGameActiveWithAnySolvedRows) && (
        <div className="grid gap-y-2 pb-2">
          {solvedGameData.map((solvedRowObj, index) => (
            <SolvedWordRow
              key={solvedRowObj.category}
              shouldAnimate={index >= initialSolvedCountRef.current && isGameActive}
              {...solvedRowObj}
            />
          ))}
        </div>
      )}
      {isGameActive && (
        <div className={`grid grid-cols-4 gap-2 md:gap-4 w-full ${shouldGridShake ? styles.shake : ""}`}>
          {flatWordsList.map((word) => (
            <motion.div
              key={word}
              layout
              transition={{
                layout: { duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }
              }}
              className="aspect-square w-full"
            >
              <WordButton
                word={word}
                fullCandidateSize={categorySize}
                isAnimating={animatingWords.includes(word)}
                isMerging={mergingWords.includes(word)}
              />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GameGrid;
